import { useTranslation } from 'react-i18next';
import type { TranslationKey } from 'src/types';
import { Badge, Button } from 'src/components/atoms';
import { BadgeVariant } from 'src/components/atoms/Badge/Badge.styles';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { ShadowsSize } from 'src/components/foundations/tokens';
import { UserReportsQuery } from 'src/graphql';
import { useTheme } from 'styled-components';
import { DateFormats, formatDate } from 'src/utils/time';
import {
  ButtonsContainer,
  CategoryLabel,
  LastButton,
  LeftSideContainer,
  MessageCountBadge,
  ReadableId,
  ReadableIdWrap,
  ReportCardContainer,
  ReportTitleDetails,
  StatusContainer,
  StyledCardContainer,
  SubmittedAt,
  TopRow,
} from './ReportCard.styled';
import { ReportCardEvidenceButton } from './ReportCardEvidenceButton';
import { ReportSubmissionMode } from './ReportSubmissionMode';

export type ReportCardSummaryType = Omit<
  NonNullable<UserReportsQuery['userReports']>[number],
  '__typename'
>;

interface Props {
  report: ReportCardSummaryType;
  version: 'report-list' | 'report-view';
  onReportOpen?: (reportInternalId: string) => void;
  onAddEvidenceSelected?: (reportInternalId: string) => void;
  onMessageCaseManagerSelected?: (reportInternalId: string) => void;
}

export const ReportCard = ({
  report,
  version,
  onReportOpen,
  onAddEvidenceSelected,
  onMessageCaseManagerSelected,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    report: reportInternalId,
    reportReadableId,
    status,
    submittedAt,
    reportedCategoryLocale,
    unreadMessagesCount,
  } = report;

  return (
    <StyledCardContainer
      $shadowSize={ShadowsSize.small}
      data-testid="report-card"
    >
      <ReportCardContainer version={version}>
        <LeftSideContainer>
          <TopRow>
            <StyledIcon
              color={
                theme.isVaultVariant
                  ? theme.newPalette.neutral.N900
                  : theme.atlas.core.color.gray[950]
              }
              className={'uil-file-alt'}
              size="24px"
            />
            <ReportTitleDetails>
              <CategoryLabel>{reportedCategoryLocale?.name}</CategoryLabel>
              <SubmittedAt>
                {t('public.PublicReportCard.submitted')}{' '}
                {formatDate(submittedAt, DateFormats.DAY_MONTH_YEAR)}
                <ReportSubmissionMode submissionMode={report.submissionMode} />
              </SubmittedAt>
            </ReportTitleDetails>

            <StatusContainer version={version}>
              <Badge
                variant={status.toLowerCase() as BadgeVariant}
                text={t(`ReportStatus.${status}` as TranslationKey)}
              />
            </StatusContainer>
          </TopRow>

          <ReadableIdWrap>
            {t('public.PublicReportCard.reportId')}{' '}
            <ReadableId>{reportReadableId}</ReadableId>
          </ReadableIdWrap>
        </LeftSideContainer>

        <ButtonsContainer>
          {onMessageCaseManagerSelected && (
            <Button
              variant="primary"
              position="relative"
              onClick={() => onMessageCaseManagerSelected(reportInternalId)}
              data-testid="message-case-manager-button"
            >
              <StyledIcon className="uil-comment" />
              {t('public.PublicReportCard.messageCaseManager')}
              {unreadMessagesCount > 0 && (
                <MessageCountBadge
                  variant="custom"
                  text={`${unreadMessagesCount}`}
                  color={
                    theme.isVaultVariant
                      ? theme.newPalette.neutral.N0
                      : theme.atlas.semantic.color.surface.default
                  }
                  background-color={
                    theme.isVaultVariant
                      ? theme.newPalette.red.R300
                      : theme.atlas.semantic.color.action.destructive.default
                  }
                />
              )}
            </Button>
          )}
          {onAddEvidenceSelected && (
            <ReportCardEvidenceButton
              onClick={() => onAddEvidenceSelected(reportInternalId)}
              reportStatus={status}
            />
          )}
          {onReportOpen && (
            <LastButton
              variant="secondary"
              onClick={() => onReportOpen(reportInternalId)}
              data-testid="open-report-button"
            >
              <StyledIcon className="uil-eye" />
              {t('public.PublicReportCard.viewReport')}
            </LastButton>
          )}
        </ButtonsContainer>
      </ReportCardContainer>
    </StyledCardContainer>
  );
};
